import {VanillaGuidedReadingStepCallback} from "../VanillaReader/VanillaReaderModel";
import {MediaType} from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import {EPUBStructuralSematicsVocabulary} from './EPUBStructuralSemanticsVocabulary';
import {
    IVanillaMediaObjectData,
    IVanillaReaderGuidedReadingStepData, IVanillaReaderGuidedReadingTimelineData,

} from "./VanillaReaderGuidedReadingModel";

/**
 * # VanillaReaderGuidedReadingStep
 *
 * ## RESPONSIBILITIES
 *
 * This class represents one "step" in the playback timeline. A step in turn contains a list of `IVanillaMediaObjectData`
 * objects that will be played back in parallel by the `VanillaReaderGuidedReadingPlayer`.
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderGuidedReadingPlayer
 * - IVanillaMediaObjectData
 *
 **/
export class VanillaReaderGuidedReadingStep {
    stepType: EPUBStructuralSematicsVocabulary;
    locator: string;
    mediaObjects: IVanillaMediaObjectData[];
    steps: VanillaReaderGuidedReadingStep[] = [];

    constructor(timelineStepData: IVanillaReaderGuidedReadingStepData, private _onStepAddedCallback?: VanillaGuidedReadingStepCallback) {
        this.stepType = timelineStepData.stepType;
        this.locator = timelineStepData.locator;
        this.mediaObjects = timelineStepData.mediaObjects;
        this.steps = [];
        timelineStepData.steps?.forEach(stepData => {
            let step = this._createStepFromData(stepData);
            this.steps.push(step);
        });
    }

    private _createStepFromData(stepData: IVanillaReaderGuidedReadingStepData): VanillaReaderGuidedReadingStep {
        let stepList: VanillaReaderGuidedReadingStep[] = [];
        const onStepAdded = (step: VanillaReaderGuidedReadingStep)=> {
            stepList.push(step);
        };

        let newStep: VanillaReaderGuidedReadingStep = new VanillaReaderGuidedReadingStep(stepData, onStepAdded);

        if(this._onStepAddedCallback) {
            this._onStepAddedCallback(newStep);

            for(let step of stepList) {
                this._onStepAddedCallback(step);
            }
        }

        return newStep;
    }
}

/**
 * # VanillaReaderGuidedReadingTimeline
 *
 * ## RESPONSIBILITIES
 *
 * This class holds a list of `VanillaReaderGuidedReadingStep` objects that will be played back in sequence by the
 * `VanillaReaderGuidedReadingPlayer`.
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderGuidedReadingStep
 *
 **/
export class VanillaReaderGuidedReadingTimeline {
    publicationId: string;
    _steps: VanillaReaderGuidedReadingStep[] = [];
    _stepsListFlat: VanillaReaderGuidedReadingStep[] = [];

    constructor(timelineData: IVanillaReaderGuidedReadingTimelineData) {
        this.publicationId = timelineData.publicationId;
        timelineData.steps.forEach(step => {
            this._steps.push(this._createStepsFromData(step));
        });
    }

    getStep(index: number): VanillaReaderGuidedReadingStep {
        return this._stepsListFlat[index];
    }

    getSteps(): VanillaReaderGuidedReadingStep[] {
        return this._stepsListFlat;
    }

    getStepsByMediaType(stepType: EPUBStructuralSematicsVocabulary): VanillaReaderGuidedReadingStep[] {
        return this._stepsListFlat.filter((step: VanillaReaderGuidedReadingStep) => step.stepType === stepType)
    }

    getStepsByMediaObjectType(mediaObjectType: MediaType): VanillaReaderGuidedReadingStep[] {
        return this._stepsListFlat.filter((step: VanillaReaderGuidedReadingStep) => step.mediaObjects.filter((mediaObjectData: IVanillaMediaObjectData)=>{
            mediaObjectData.mediaType === mediaObjectType;
        }))
    }

    private _createStepsFromData(stepData: IVanillaReaderGuidedReadingStepData): VanillaReaderGuidedReadingStep {
        let childSteps:VanillaReaderGuidedReadingStep[] = [];
        let newStep: VanillaReaderGuidedReadingStep = new VanillaReaderGuidedReadingStep(stepData, ((childStep: VanillaReaderGuidedReadingStep) => {
            childSteps.push(childStep);
        }));

        this._stepsListFlat.push(newStep);
        this._stepsListFlat.push(...childSteps);

        return newStep;
    }

}
